<template>
  <div class="w-full flex flex-col rounded-xl" style="background-color: #10111A"
  :class="rankData.previous_rank ? 'pb-1' : 'py-1'">
    <!-- Graph Area -->
    <div v-if="rankData.previous_rank" class="relative w-full overflow-x-hidden" style="height: 96px;">
      <div class="absolute top-0 right-0 bottom-1" style="left: -27%;">
        <div ref="graphArea" class="pointer-events-none" />
      </div>
    </div>
    <div class="w-full flex flex-col px-1">
      <!-- Category Label -->
      <div v-if="categoryLabel" class="w-full flex items-center justify-center p-1 my-1 rounded-md"
      :style="{ backgroundColor: categoryLabel.color.background }">
        <component :is="categoryLabel.icon" :style="{ color: categoryLabel.color.icon }" />
        <BaseText type="label" size="sm" class="px-1" :style="{ color: categoryLabel.color.text }">
          {{ categoryLabel.label }}
        </BaseText>
      </div>
      <!-- Ad Rank -->
      <div class="w-full flex items-center p-2">
        <AdRankIcon class="text-neutral-alpha-650" />
        <BaseText type="body" size="sm" class="pl-2 text-neutral-alpha-650">
          Rank
        </BaseText>
        <BaseText type="label" size="sm" class="ml-auto text-white">
          #{{ rankData.rank }}
        </BaseText>
        <BaseText type="body" size="sm" class="ml-2 pr-1 text-neutral-alpha-700">
          of {{ rankData.total_ads.toLocaleString() }}
        </BaseText>
      </div>
      <!-- Percentile -->
      <div class="w-full flex items-center p-2">
        <PercentileIcon class="text-neutral-alpha-650" />
        <BaseText type="body" size="sm" class="pl-2 text-neutral-alpha-650">
          Percentile
        </BaseText>
        <BaseText type="body" size="sm" class="ml-auto pr-1 text-neutral-alpha-700">
          {{ percentileLabel }}
        </BaseText>
      </div>
      <!-- Trend -->
      <div v-if="rankData.previous_rank" class="w-full flex items-center p-2">
        <TrendIcon class="text-neutral-alpha-650" />
        <BaseText type="body" size="sm" class="pl-2 text-neutral-alpha-650">
          Trend (7d)
        </BaseText>
        <div class="ml-auto flex items-center gap-2 px-1">
          <BaseText type="body" size="sm" class="text-neutral-alpha-700">
            #{{ rankData.previous_rank }}
          </BaseText>
          <ArrowIcon class="text-neutral-alpha-500" />
          <BaseText type="label" size="sm" class="text-white">
            #{{ rankData.rank }}
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApexCharts from 'apexcharts'

// Icons
import BreakoutIcon from '@/components/globals/Icons/SpyderV2Icons/BreakoutIcon.vue'
import EvergreenIcon from '@/components/globals/Icons/SpyderV2Icons/EvergreenIcon.vue'
import AdRankIcon from '@/components/globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import PercentileIcon from '@/components/globals/Icons/PercentileIcon.vue'
import TrendIcon from '@/components/globals/Icons/SpyderV2Icons/TrendIcon.vue'
import ArrowIcon from '@/components/globals/Icons/ArrowIcon.vue'

// How fast the graph approaches its max offset based on rank delta
const CHANGE_SCALE_FACTOR = 0.15

export default {
  name: 'ActiveRanking',
  components: {
    AdRankIcon,
    PercentileIcon,
    TrendIcon,
    ArrowIcon
  },
  props: {
    /*{
      rank: Number,
      previous_rank: Number,
      total_ads: Number,
      category: String | null
    }*/
    rankData: {
      type: Object,
      required: true
    }
  },
  watch: {
    seriesValues: {
      handler () {
        this.initializeLineGraph()
      },
      immediate: true
    }
  },
  beforeDestroy () {
    const chart = this.chart
    this.chart = null
    // Delay the chart teardown so it doesn't flash during fade out transitions
    if (chart) setTimeout(() => chart.destroy(), 200)
  },
  computed: {
    seriesValues () {
      const { rank, previous_rank } = this.rankData
      if (!previous_rank) return []

      const delta = previous_rank - rank
      const offsetFactor = 1 - Math.pow(Math.E, -1 * CHANGE_SCALE_FACTOR * Math.abs(delta))

      if (delta > 0) return [0, 0, offsetFactor]
      else if (delta < 0) return [offsetFactor, offsetFactor, 0]
      else return [0.2, 0.2, 0.2]
    },
    pointLabels () {
      const { previous_rank, rank } = this.rankData
      if (!previous_rank) return []
      return ['', `${previous_rank}`, `${rank}`]
    },
    categoryLabel () {
      const { category } = this.rankData
      switch (category) {
        case 'breakout':
          return {
            label: 'Breakout',
            icon: BreakoutIcon,
            color: { icon: '#84BDF6', text: '#C0E4FF', background: 'rgba(0, 107, 255, 0.12)'}
          }
        case 'evergreen':
          return {
            label: 'Evergreen',
            icon: EvergreenIcon,
            color: { icon: '#E0F2D4', text: '#DCF3D1', background: 'rgba(97, 190, 70, 0.12)'}
          }
        default: return null
      }
    },
    graphColor () {
      const { category } = this.rankData
      switch (category) {
        case 'breakout': return '#006BFF'
        case 'evergreen': return '#79bc57'
        default: return '#d9d9da'
      }
    },
    percentileLabel () {
      const { rank, total_ads } = this.rankData
      const percentile = Math.ceil((rank / total_ads) * 100)
      return isNaN(percentile) ? '--' : `${percentile}%`
    }
  },
  methods: {
    initializeLineGraph () {
      if (!this.seriesValues?.length) return
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }

      const options = {
        series: [{
          name: 'Rank Trend',
          data: this.seriesValues
        }],
        colors: [this.graphColor],
        chart: {
          type: 'area',
          height: '100%',
          toolbar: { show: false },
          zoom: { enabled: false },
          offsetY: 0,
          offsetX: 0,
          sparkline: { enabled: true },
          animations: { enabled: false }
        },
        stroke: {
          curve: 'straight',
          width: 2,
          lineCap: 'round'
        },
        fill: {
          type: 'gradient',
          gradient: {
            type: 'vertical',
            gradientToColors: [this.graphColor],
            opacityFrom: 0.4,
            opacityTo: 0.1,
          }
        },
        grid: {
          show: false,
          padding: { left: 0, right: 12, top: 30, bottom: 8 }
        },
        markers: {
          size: 0, // hide markers by default
          discrete: [
            {
              seriesIndex: 0,
              dataPointIndex: 1,
              fillColor: '#10111A',
              strokeColor: this.graphColor,
              size: 4,
              strokeWidth: 4
            },
            {
              seriesIndex: 0,
              dataPointIndex: 2,
              fillColor: '#10111A',
              strokeColor: this.graphColor,
              size: 4,
              strokeWidth: 4
            }
          ]
        },
        dataLabels: { 
          enabled: true,
          offsetY: -8,
          formatter: (_, { dataPointIndex }) => this.pointLabels[dataPointIndex],
          style: {
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: 'Inter',
            colors: ['rgba(255, 255, 255, 0.68)']
          },
          background: { enabled: false },
          dropShadow: { enabled: false },
        },
        tooltip: { enabled: false },
        legend: { enabled: false },
        yaxis: {
          show: false,
          min: -0.2,
          max: 1.2,
          forceNiceScale: false
        },
        xaxis: { type: 'numeric', labels: { show: false } }
      }
      this.$nextTick(() => {
        const chartContainer = this.$refs.graphArea
        this.chart = new ApexCharts(chartContainer, options)
        this.chart.render().then(() => {
          const labels = chartContainer.querySelectorAll('.apexcharts-datalabel')
          const last = labels[labels.length - 1]
          if (last) last.setAttribute('fill', '#FFFFFF')
        })
      })
    }
  }
}
</script>